import {
  Button,
  Divider,
  mergeClasses,
  Switch,
  tokens,
} from '@fluentui/react-components';
import {
  EditFilled,
  EditRegular,
  HomePersonFilled,
  HomePersonRegular,
  TaskListSquareLtrFilled,
  TaskListSquareLtrRegular,
  AppsFilled,
  AppsRegular,
  NavigationFilled,
  PhoneDesktopRegular,
} from '@fluentui/react-icons';
import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSelector } from '@tdp';
import { Box, Flex } from '@tdp/common/fluentV9MigrationComponents';
import { onActivate, ScreenSizes, useScreenSize } from '@tdp/common/shared';
import { themeSlice } from '@tdp/common/theme';
import { loadBuildConfig, DeveloperPortalRootContext } from '@tdp/core';

import { useStyles } from './LeftNav.Styles';
import { MenuFooter } from './MenuFooter';
import {
  getSelectedKey,
  INavigationGroup,
  NavMenuItemKeys,
  Routes,
} from './utils';

export const LeftNav: React.FC<{
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setIsExpanded, isExpanded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const smallScreen = useScreenSize(ScreenSizes.Small);
  const classes = useStyles();
  const currentTheme = useSelector(themeSlice.selectors.getCurrentTheme);
  const intl = useIntl();

  const toggleNavSection = () => {
    setIsExpanded(!isExpanded);
  };

  const onNavMenuItemClick = (
    _event: React.MouseEvent<HTMLElement>,
    _id: string,
    navLink: string
  ) => {
    navigate(`/${navLink.replace(/\//gi, '')}`);
    if (smallScreen) {
      // when in small screen where header sidebar and main grid items are vertically aligned,
      // collapsing the nav menu section to show the main content
      setIsExpanded(!isExpanded);
    }
  };

  const selectedKey = getSelectedKey(location.pathname);

  const navMenuItems = sideBarNavigationItems.map((link) => {
    const isActive = selectedKey === link.key;
    return (
      <Flex
        id={link.id}
        key={link.key}
        className={mergeClasses(
          classes.linkContainer,
          isActive && classes.linkActive,
          currentTheme === 'contrast' && classes.linkContainerContrast
        )}
        vAlign="center"
        role="tab"
        aria-selected={isActive}
        aria-label={link.key}
        {...onActivate((e: React.MouseEvent | React.KeyboardEvent) => {
          onNavMenuItemClick(
            e as React.MouseEvent<HTMLElement>,
            link.id,
            link.route
          );
        })}
      >
        <Box> {link.getIcon(!isActive)} </Box>
        <Box className={classes.text}>
          {(smallScreen || isExpanded) && link.content}
        </Box>
      </Flex>
    );
  });

  const navMenuItemsSection = () => (
    <Flex className={mergeClasses(classes.navContainer)} role="tablist">
      {navMenuItems}
    </Flex>
  );

  return (
    <Flex
      id="NavContentContainer"
      column
      space={isExpanded ? 'between' : undefined}
      fill
      className={classes.SidebarContainer}
      role="navigation"
    >
      <div>
        <Button
          appearance="transparent"
          role="button"
          aria-expanded={isExpanded}
          aria-label={intl.formatMessage({
            defaultMessage: 'Main navigation menu.',
            description: 'aria-label for navigation menu expander button',
          })}
          className={classes.MenuIconContainer}
          {...onActivate(toggleNavSection)}
          icon={<NavigationFilled className={classes.MenuIcon} />}
          data-testid="navigation-menu-expander-button"
        />
        {/* For small screen toggleNavSection should collapse the nav menu-items */}
        {smallScreen
          ? isExpanded && navMenuItemsSection()
          : navMenuItemsSection()}
      </div>
      {/* Only show Menufooter if navSection is expanded */}
      {isExpanded && <MenuFooter horizontal={smallScreen} />}
    </Flex>
  );
};

export const sideBarNavigationItems: INavigationGroup[] = [
  {
    content: 'Home',
    getIcon: (active: boolean) => {
      return active ? (
        <HomePersonRegular fontSize={20} />
      ) : (
        <HomePersonFilled fontSize={20} color={tokens.colorBrandForeground1} />
      );
    },
    route: Routes.Home,
    key: NavMenuItemKeys.Home,
    id: 'NavigationMainHome',
  },
  {
    content: 'Apps',
    getIcon: (active: boolean) => {
      return active ? (
        <AppsRegular fontSize={20} />
      ) : (
        <AppsFilled fontSize={20} color={tokens.colorBrandForeground1} />
      );
    },
    route: Routes.Apps,
    key: NavMenuItemKeys.Apps,
    id: 'NavigationMainApps',
  },
  {
    content: 'Tools',
    getIcon: (active: boolean) => {
      return active ? (
        <EditRegular fontSize={20} />
      ) : (
        <EditFilled fontSize={20} color={tokens.colorBrandForeground1} />
      );
    },
    route: Routes.Tools,
    key: NavMenuItemKeys.Tools,
    id: 'NavigationMainTools',
  },
  // {
  //   content: 'Changelogs',
  //   getIcon: (active: boolean) => {
  //     return active ? (
  //       <TaskListSquareLtrRegular fontSize={20} />
  //     ) : (
  //       <TaskListSquareLtrFilled
  //         fontSize={20}
  //         color={tokens.colorBrandForeground1}
  //       />
  //     );
  //   },
  //   route: Routes.Changelog,
  //   key: NavMenuItemKeys.Changelogs,
  //   id: 'NavigationMainChangelogs',
  // },
];
